import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { CONTACT } from "@/config/contact";
import { buildSeoMeta } from "@/lib/seo";
import { buildBreadcrumbJsonLd } from "@/lib/breadcrumb-jsonld";
import { buildReviewJsonLd, buildAutoDealerJsonLd } from "@/lib/jsonld";
import { formatSyncedHint } from "@/lib/utils";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  HandshakeIcon,
  ShieldCheck,
  Search,
  Banknote,
  MapPin,
  Phone,
  Mail,
  Clock,
  ArrowRight,
  Star,
} from "lucide-react";
import logoMark from "@/assets/logo-white.svg";
import { HeroBanner } from "@/components/HeroBanner";
import { useHeroSettings } from "@/hooks/use-hero-settings";

const BREADCRUMB = buildBreadcrumbJsonLd([
  { name: "Forside", path: "" },
  { name: "Om os", path: "om-os" },
]);

// Static stats kept as defaults — overridden with live counts where possible
// (active cars from `cars` table, testimonial count from local array).
const STATIC_STATS = {
  yearsInBusiness: "15+",
} as const;

// Format a 0-5 rating as Danish "4,8" with comma decimal.
function formatRating(avg: number): string {
  return avg.toFixed(1).replace(".", ",");
}

// Round count down to nearest "nice" milestone so a single new car doesn't
// bump the displayed number, and "47" doesn't read as overly precise.
function roundToMilestone(n: number): string {
  // Under 25 biler: vis præcist tal (mere ærligt på et lille lager).
  if (n < 25) return `${n}`;
  if (n < 100) return `${Math.floor(n / 25) * 25}+`;
  return `${Math.floor(n / 50) * 50}+`;
}




const VALUES = [
  {
    icon: Search,
    title: "Håndplukket udvalg",
    body: "Vi gennemgår hver bil personligt før køb. Cirka halvdelen af de biler vi ser på, frasorterer vi.",
  },
  {
    icon: HandshakeIcon,
    title: "Fair pris uden pres",
    body: "Vi forhandler ikke aggressivt. Vores priser er sat så de er fair fra start — også hvis du ikke køber.",
  },
  {
    icon: ShieldCheck,
    title: "Fuld gennemsigtighed",
    body: "Historik, tilstand og udstyr — du får alle detaljer på bordet før du beslutter dig.",
  },
  {
    icon: Banknote,
    title: "Finansiering & garanti",
    body: "Lånetilbud samme dag, 2 års reklamationsret og udvidet garanti via tredjepart.",
  },
] as const;

// Team — billeder er midlertidige logo-placeholders indtil rigtige portrætter er klar.
const TEAM = [
  { name: "Simon Snedker", role: "Salg & Indkøb" },
  { name: "Mads Hartmann", role: "Head of Marketing" },
  { name: "Loui", role: "Office Dog" },
] as const;

type Testimonial = {
  id: string;
  customer_name: string;
  customer_location: string | null;
  quote: string;
  rating: number;
  car_purchased: string | null;
  created_at: string;
};


const FAQS: { q: string; a: string }[] = [
  {
    q: "Hvor ligger AutoXChange, og hvad er åbningstiderne?",
    a: `Vi holder til på ${CONTACT.addressLine}. Du finder vores aktuelle åbningstider nederst på siden — og du er altid velkommen til at aftale et tidspunkt uden for åbningstid.`,
  },
  {
    q: "Tager I min gamle bil i bytte?",
    a: "Ja. Vi giver gerne et fair bud på din nuværende bil som en del af handlen. Send os bilens registreringsnummer og et par billeder, så vender vi tilbage med en konkret vurdering — uden forpligtelser.",
  },
  {
    q: "Kan jeg få bilen leveret hjem til døren?",
    a: "Ja, vi leverer biler i hele Danmark. Pris afhænger af afstand og om der er en byttebil med retur. Vi aftaler det altid på forhånd, så der ikke er overraskelser.",
  },
  {
    q: "Hvilken garanti følger med bilen?",
    a: "Som forbruger har du 2 års reklamationsret jf. købeloven. Derudover tilbyder vi udvidet garanti via anerkendt tredjepart på de fleste biler — vi gennemgår betingelserne sammen med dig før handlen.",
  },
  {
    q: "Kan jeg finansiere bilen igennem jer?",
    a: "Ja. Vi samarbejder med flere bilfinansieringsselskaber og kan typisk give dig et lånetilbud samme dag. Vi rådgiver objektivt og presser dig aldrig til at vælge finansiering hos os.",
  },
  {
    q: "Tilbyder I leasing?",
    a: "Ja, vi har både privat- og erhvervsleasing på udvalgte biler. Kig efter leasing-mærket på bilkortene, eller kontakt os for et skræddersyet tilbud baseret på dit kørselsbehov.",
  },
  {
    q: "Er bilen synet og klargjort før salg?",
    a: "Ja. Hver bil gennemgår en teknisk kontrol, bliver klargjort og leveres med nyt syn, hvis der er behov. Vi sælger kun biler vi selv ville køre i — derfor frasorterer vi cirka halvdelen af dem vi ser på.",
  },
  {
    q: "Kan jeg prøvekøre bilen?",
    a: "Selvfølgelig. Vi opfordrer altid til en ordentlig prøvetur før beslutningen tages. Medbring kørekort, så er du klar — book gerne tid på forhånd, så bilen er klar når du kommer.",
  },
  {
    q: "Hvad gør jeg hvis bilen får en fejl efter købet?",
    a: "Kontakt os først — så finder vi en løsning sammen. Vores reklamationsret dækker mangler, der var til stede ved overtagelsen, og vi tager reklamationer alvorligt. Vi vil hellere have en glad kunde end en hurtig handel.",
  },
  {
    q: "Hvordan betaler jeg for bilen?",
    a: "Bilen betales typisk via bankoverførsel inden udlevering. Vi modtager ikke kontantbetaling for beløb over 19.999 kr (hvidvaskloven). Finansiering og leasing aftales separat før udlevering.",
  },
];

const FAQ_JSONLD = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

const BREADCRUMB_JSON_LD = buildBreadcrumbJsonLd([
  { name: "Forside", path: "" },
  { name: "Om os", path: "om-os" },
]);

export const Route = createFileRoute("/om-os")({
  head: () => {
    const seo = buildSeoMeta({
      title: "Om AutoXChange · Bilforhandler ved Vejle & Kolding",
      description:
        "Uafhængig bilforhandler i Børkop, midt i Trekantområdet mellem Vejle, Kolding og Fredericia. Vi håndplukker hver bil og står ved det, vi sælger.",
      path: "/om-os",
      imageAlt: "AutoXChange — bilforhandler i Trekantområdet (Børkop, ved Vejle og Kolding)",
      geo: {
        region: "DK-83",
        placename: "Børkop",
        latitude: 55.6379,
        longitude: 9.6431,
      },
    });
    return {
      ...seo,
      scripts: [
        { type: "application/ld+json", children: BREADCRUMB_JSON_LD },
        { type: "application/ld+json", children: buildAutoDealerJsonLd() },
      ],
    };
  },
  component: OmOs,
});

function OmOs() {
  const mapEmbed = `https://www.google.com/maps?q=${encodeURIComponent(CONTACT.addressLine)}&output=embed`;
  const { data: hero } = useHeroSettings("about");

  // Lazy-load Google Maps iframe: only mount when the map section is near the
  // viewport. Saves ~300 KB of network + heavy JS on initial paint, and the
  // iframe never blocks LCP/INP for users who never scroll to the footer area.
  // Server always renders the placeholder (mapVisible=false) and the client
  // does the same on first paint to avoid hydration mismatch — only after
  // mount do we attach the IntersectionObserver and flip to the iframe.
  const mapWrapRef = useRef<HTMLDivElement | null>(null);
  const [mapVisible, setMapVisible] = useState(false);
  useEffect(() => {
    const node = mapWrapRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setMapVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setMapVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: "300px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  // Live count of active cars currently for sale.
  // NOTE: Avoid `head: true` — combined with `count=exact` it triggers a HEAD
  // request that the Supabase edge sometimes aborts on (CORS/preflight issues
  // with publishable keys). A tiny SELECT with `count: "exact"` is reliable.
  const { data: activeCarsCount } = useQuery({
    queryKey: ["om-os", "active-cars-count"],
    queryFn: async () => {
      const { count } = await supabase
        .from("cars")
        .select("id", { count: "exact" })
        .eq("status", "active")
        .eq("is_demo", false)
        .limit(1);
      return count ?? 0;
    },
    staleTime: 5 * 60 * 1000, // 5 min
  });

  // Most recent Bilinfo sync timestamp — drives the "Opdateret X siden" hint.
  const { data: lastSyncedAt } = useQuery({
    queryKey: ["om-os", "last-synced-at"],
    queryFn: async () => {
      const { data } = await supabase
        .from("cars")
        .select("last_synced_at")
        .not("last_synced_at", "is", null)
        .order("last_synced_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      return data?.last_synced_at ?? null;
    },
    staleTime: 60 * 1000, // 1 min — keep the relative label fresh
    refetchInterval: 60 * 1000,
  });

  // Live testimonials managed via /admin/anmeldelser
  const { data: testimonials } = useQuery({
    queryKey: ["om-os", "testimonials"],
    queryFn: async () => {
      const { data } = await supabase
        .from("testimonials")
        .select("id, customer_name, customer_location, quote, rating, car_purchased, created_at")
        .eq("is_published", true)
        .order("display_order", { ascending: true })
        .order("created_at", { ascending: false });
      return (data ?? []) as Testimonial[];
    },
    staleTime: 5 * 60 * 1000,
  });

  // Live tilfredshed: gennemsnit af publicerede testimonial-ratings.
  const reviewCount = testimonials?.length ?? 0;
  const averageRating =
    reviewCount > 0
      ? (testimonials ?? []).reduce((sum, t) => sum + (t.rating ?? 0), 0) / reviewCount
      : 0;

  const stats: { value: string; label: string; hint?: string }[] = [
    { value: STATIC_STATS.yearsInBusiness, label: "År i branchen", hint: "Siden 2010" },
    {
      value: activeCarsCount && activeCarsCount > 0 ? roundToMilestone(activeCarsCount) : "—",
      label: "Biler på lager",
      hint: formatSyncedHint(lastSyncedAt),
    },
    {
      value: reviewCount > 0 ? `${formatRating(averageRating)}★` : "—",
      label: "Kundetilfredshed",
      hint:
        reviewCount > 0
          ? `Snit af ${reviewCount} ${reviewCount === 1 ? "anmeldelse" : "anmeldelser"}`
          : "Ingen anmeldelser endnu",
    },
    {
      value: `${reviewCount}`,
      label: "Kundeanmeldelser",
      hint: "Verificerede",
    },
  ];

  // AggregateRating JSON-LD — refererer til AutoDealer via @id så Google
  // kobler stjernerne til virksomheden og kan vise dem i søgeresultater.
  // Bruger nøjagtig samme tal som vises i stats-stripen.
  const aggregateRatingJsonLd =
    reviewCount > 0
      ? JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AutoDealer",
          "@id": `${CONTACT.websiteUrl.replace(/\/$/, "")}/#dealer`,
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: Number(averageRating.toFixed(1)),
            reviewCount,
            bestRating: 5,
            worstRating: 1,
          },
        })
      : null;

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: BREADCRUMB }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: FAQ_JSONLD }} />
      {aggregateRatingJsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: aggregateRatingJsonLd }} />
      )}
      {(testimonials ?? []).map((t) => (
        <script
          key={`review-jsonld-${t.id}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: buildReviewJsonLd({
              id: t.id,
              author: t.customer_name,
              authorLocation: t.customer_location,
              rating: t.rating,
              quote: t.quote,
              carPurchased: t.car_purchased,
              datePublished: t.created_at,
            }),
          }}
        />
      ))}

      {/* Image hero with overlay — admin-controlled (settings key hero_about) */}
      <HeroBanner
        eyebrow="Bilforhandler i Trekantområdet"
        title={
          <>
            Bilforhandler ved Vejle og Kolding —{" "}
            <span className="text-accent">drevet af passion</span>, ikke pres.
          </>
        }
        subtitle="AutoXChange er en uafhængig bilforhandler i Børkop, midt i Trekantområdet. Vi håndplukker hver bil personligt — fordi vi kun sælger det, vi selv ville køre i."
        image={hero?.url}
        mediaType={hero?.mediaType}
        minHeightClass="min-h-[60vh]"
        cta={{ label: "Se biler til salg", href: "/biler-til-salg" }}
      />

      {/* Stats stripe */}
      <section className="border-y border-border bg-card">
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-px bg-border md:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} className="bg-card px-3 py-6 text-center sm:px-6 sm:py-8">
              <p className="text-2xl font-bold text-accent sm:text-3xl md:text-4xl">{s.value}</p>
              <p className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground sm:text-xs md:text-sm">
                {s.label}
              </p>
              {s.hint && (
                <p className="mt-1.5 text-[10px] leading-tight text-muted-foreground/80 sm:text-[11px] md:text-xs">
                  {s.hint}
                </p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Story */}
      <section className="bg-background py-20">
        <div className="mx-auto grid max-w-6xl gap-12 px-6 md:grid-cols-2 md:gap-16">
          <div>
            <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent">
              Vores historie
            </p>
            <h2 className="text-3xl font-bold leading-tight md:text-4xl">
              En uafhængig bilforhandler — bygget på tillid.
            </h2>
          </div>
          <div className="space-y-5 text-base leading-relaxed text-muted-foreground">
            <p>
              AutoXChange startede med en simpel filosofi: vi sælger kun biler, vi selv ville køre i.
              I dag betjener vi kunder i hele Danmark fra vores plads i Børkop.
            </p>
            <p>
              Vi tror på fair priser og fuld gennemsigtighed. Du får alle detaljer om bilen —
              historik, tilstand og udstyr — uden gråzoner. Vi presser ingen, og vi anbefaler altid
              en prøvetur før beslutningen tages.
            </p>
            <p>
              Du er velkommen til at <Link to="/kontakt" className="text-accent underline-offset-4 hover:underline">kontakte os</Link> for en uformel snak — også uden for åbningstid efter aftale.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="border-t border-border bg-muted/30 py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="mb-12 text-center">
            <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent">
              Sådan arbejder vi
            </p>
            <h2 className="text-3xl font-bold md:text-4xl">Fire principper, ingen undtagelser</h2>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v) => {
              const Icon = v.icon;
              return (
                <div
                  key={v.title}
                  className="group relative overflow-hidden rounded-xl border border-border bg-card p-6 transition-all hover:border-accent/40 hover:shadow-lg"
                >
                  <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-accent/10 text-accent transition-colors group-hover:bg-accent group-hover:text-accent-foreground">
                    <Icon size={22} />
                  </div>
                  <h3 className="mb-2 text-lg font-semibold text-foreground">{v.title}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">{v.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="bg-background py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="mb-12 text-center">
            <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent">
              Mød holdet
            </p>
            <h2 className="text-3xl font-bold md:text-4xl">Mennesker bag handlen</h2>
            <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
              Vi er et lille team — og det betyder du altid taler med samme person, fra første henvendelse
              til nøgleudlevering.
            </p>
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {TEAM.map((m) => (
              <div key={m.name} className="group">
                <div className="relative flex aspect-[4/5] items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-primary via-primary to-primary/80 ring-1 ring-border">
                  <img
                    src={logoMark}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    decoding="async"
                    width={320}
                    height={140}
                    className="w-2/5 max-w-[180px] opacity-80"
                  />
                  <span className="sr-only">
                    Portræt af {m.name} kommer snart
                  </span>
                </div>
                <div className="mt-4">
                  <p className="text-lg font-semibold text-foreground">{m.name}</p>
                  <p className="text-sm text-accent">{m.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer testimonials — social proof under team to reinforce trust
          right after readers see "the people behind the deal". */}
      <section className="border-t border-border py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="mb-10 text-center">
            <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent">
              Hvad kunderne siger
            </p>
            <h2 className="text-3xl font-bold md:text-4xl">Anbefalet af 200+ kunder</h2>
            <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
              Et udvalg af reaktioner fra kunder, der har handlet hos os de seneste år.
            </p>
          </div>
          {testimonials && testimonials.length > 0 ? (
            <div className="grid gap-6 md:grid-cols-3">
              {testimonials.map((t) => (
                <figure
                  key={t.id}
                  className="flex h-full flex-col rounded-xl border border-border bg-card p-6 shadow-sm"
                >
                  <div
                    className="mb-4 flex items-center gap-0.5 text-accent"
                    aria-label={`${t.rating} ud af 5 stjerner`}
                  >
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <Star key={i} size={16} className="fill-current" />
                    ))}
                  </div>
                  <blockquote className="flex-1 text-sm leading-relaxed text-foreground">
                    &ldquo;{t.quote}&rdquo;
                  </blockquote>
                  <figcaption className="mt-5 border-t border-border pt-4 text-sm">
                    <span className="font-semibold text-foreground">{t.customer_name}</span>
                    {t.customer_location && (
                      <span className="ml-1.5 text-muted-foreground">· {t.customer_location}</span>
                    )}
                  </figcaption>
                </figure>
              ))}
            </div>
          ) : null}
        </div>
      </section>

      <section className="border-t border-border bg-muted/30 py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="mb-10 text-center">
            <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent">
              Find os
            </p>
            <h2 className="text-3xl font-bold md:text-4xl">Kig forbi — eller book en tid</h2>
          </div>
          <div className="grid gap-6 lg:grid-cols-5">
            <div
              ref={mapWrapRef}
              className="overflow-hidden rounded-xl border border-border bg-card lg:col-span-3"
              style={{ minHeight: 420 }}
            >
              {mapVisible ? (
                <iframe
                  src={mapEmbed}
                  title="Kort over AutoXChange — Industrivej 127, Børkop"
                  width="100%"
                  height="420"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              ) : (
                <div
                  aria-hidden="true"
                  className="flex h-[420px] w-full items-center justify-center text-xs text-muted-foreground"
                >
                  Kort indlæses…
                </div>
              )}
            </div>
            <div className="space-y-4 lg:col-span-2">
              <div className="rounded-xl border border-border bg-card p-6">
                <div className="mb-3 flex items-center gap-2 text-accent">
                  <MapPin size={18} />
                  <h3 className="font-semibold text-foreground">Adresse</h3>
                </div>
                <p className="text-sm text-muted-foreground">{CONTACT.addressLine}</p>
                <a
                  href={CONTACT.mapsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-flex items-center gap-1 text-sm text-accent hover:underline"
                >
                  Få rutevejledning <ArrowRight size={14} />
                </a>
              </div>

              <div className="rounded-xl border border-border bg-card p-6">
                <div className="mb-3 flex items-center gap-2 text-accent">
                  <Clock size={18} />
                  <h3 className="font-semibold text-foreground">Åbningstider</h3>
                </div>
                <ul className="space-y-1.5 text-sm">
                  {CONTACT.openingHours.map((o) => (
                    <li key={o.label} className="flex justify-between gap-3">
                      <span className="text-muted-foreground">{o.label}</span>
                      <span className="font-medium text-foreground">{o.hours}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-3 text-xs text-muted-foreground">{CONTACT.openingHoursNote}</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Button asChild variant="outline" size="lg">
                  <a href={CONTACT.phoneHref}>
                    <Phone size={16} className="mr-1.5" /> Ring
                  </a>
                </Button>
                <Button asChild size="lg">
                  <a href={CONTACT.emailHref}>
                    <Mail size={16} className="mr-1.5" /> Skriv
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-border bg-background py-20">
        <div className="mx-auto max-w-3xl px-6">
          <div className="mb-10 text-center">
            <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent">FAQ</p>
            <h2 className="text-3xl font-bold tracking-tight md:text-4xl">
              Ofte stillede spørgsmål
            </h2>
            <p className="mt-3 text-muted-foreground">
              Det vi oftest bliver spurgt om — fra prøvetur til levering og garanti.
            </p>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((faq, i) => (
              <AccordionItem key={i} value={`item-${i}`}>
                <AccordionTrigger className="text-left text-base font-medium">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="leading-relaxed text-muted-foreground">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <p className="mt-10 text-center text-sm text-muted-foreground">
            Fandt du ikke svar?{" "}
            <Link to="/kontakt" className="text-accent underline-offset-4 hover:underline">
              Skriv til os
            </Link>{" "}
            — vi svarer typisk samme dag.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden bg-foreground py-16 text-background">
        <div className="mx-auto flex max-w-4xl flex-col items-center justify-between gap-6 px-6 text-center md:flex-row md:text-left">
          <div>
            <h2 className="text-2xl font-bold md:text-3xl">Klar til at finde din næste bil?</h2>
            <p className="mt-2 text-background/70">
              Se vores aktuelle udvalg — eller book en uforpligtende prøvetur.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" variant="secondary">
              <Link to="/biler-til-salg">Se biler til salg</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-background/30 bg-transparent text-background hover:bg-background hover:text-foreground"
            >
              <Link to="/kontakt">Kontakt os</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
